import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type WheelEvent as ReactWheelEvent,
} from 'react'
import { useTranslation } from 'react-i18next'
import {
  clampDisplayIndex,
  clampLoopInternal,
  displayIndexFromInternal,
  initialInternalIndex,
  internalIndexFromDisplay,
} from '../utils/photoCarouselLoop'
import { readCarouselTrackTranslateX } from '../utils/readCarouselTrackTranslateX'
import { publicAssetUrl } from '../utils/assetUrl'
import { globalDefectNumber } from '../utils/defectMarks'

export type ListingCarouselPhoto = {
  id: number
  thumbnail_url: string
  display_url?: string
}

type DefectMark = { x: number; y: number; label: string; photo_index?: number }

type Props = {
  photos: ListingCarouselPhoto[]
  activePhoto: number
  onActivePhotoChange: (index: number) => void
  onPhotoClick?: (index: number) => void
  defectMarks?: DefectMark[]
  enlargeHint?: string
  staticPreview?: boolean
}

const IOS_GALLERY_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)'
const SNAP_RATIO = 0.2
const SNAP_MIN_PX = 44
const TAP_MOVE_MAX_PX = 8
const AXIS_LOCK_PX = 8
const WHEEL_STEP_PX = 48

type DragStart = {
  x: number
  y: number
  pointerId: number
  axis: 'x' | 'y' | null
}

export default function ListingDetailPhotoCarousel({
  photos,
  activePhoto,
  onActivePhotoChange,
  onPhotoClick,
  defectMarks = [],
  enlargeHint,
  staticPreview = false,
}: Props) {
  const { t } = useTranslation()
  const viewportRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const [viewportWidth, setViewportWidth] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const dragStartRef = useRef<DragStart | null>(null)
  const dragPxRef = useRef(0)
  const lastDragPxRef = useRef(0)
  const suppressClickRef = useRef(false)
  const activePhotoRef = useRef(activePhoto)
  const internalSlideRef = useRef(initialInternalIndex(photos.length))
  const viewportWidthRef = useRef(0)
  const wheelAccumRef = useRef(0)
  const animatingRef = useRef(false)
  const [openDefectKey, setOpenDefectKey] = useState<string | null>(null)
  const rafRef = useRef<number | null>(null)
  const animateRafRef = useRef<number | null>(null)
  activePhotoRef.current = activePhoto
  viewportWidthRef.current = viewportWidth

  const photosKey = useMemo(() => photos.map((p) => p.id).join('\u0001'), [photos])
  const prevPhotosKeyRef = useRef(photosKey)
  const prevSyncedActiveRef = useRef(activePhoto)
  /** Listing detail uses finite carousel — infinite clone track showed the wrong slide before layout. */
  const loop = false

  const slidePhotos = useMemo(() => {
    if (!loop) return photos
    return [photos[photos.length - 1], ...photos, photos[0]]
  }, [photosKey, loop, photos])

  useEffect(() => {
    setOpenDefectKey(null)
  }, [activePhoto, photosKey])

  useEffect(() => {
    const el = viewportRef.current
    if (!el || photos.length <= 1) return
    const onTouchMove = (ev: TouchEvent) => {
      if (dragStartRef.current?.axis === 'x') ev.preventDefault()
    }
    el.addEventListener('touchmove', onTouchMove, { passive: false })
    return () => el.removeEventListener('touchmove', onTouchMove)
  }, [photos.length])

  useEffect(() => {
    const el = viewportRef.current
    if (!el) return
    const measure = () => setViewportWidth(el.clientWidth)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const paintTrack = useCallback((animate: boolean) => {
    const track = trackRef.current
    const w = viewportWidthRef.current
    if (!track || w <= 0) return
    const index = loop ? internalSlideRef.current : activePhotoRef.current
    const x = -index * w + dragPxRef.current
    if (animate) animatingRef.current = true
    else animatingRef.current = false
    track.style.transition = animate ? `transform 0.48s ${IOS_GALLERY_EASE}` : 'none'
    track.style.transform = `translate3d(${x}px, 0, 0)`
  }, [loop])

  const animateTrackTo = useCallback((targetInternal: number) => {
    const track = trackRef.current
    const w = viewportWidthRef.current
    if (!track || w <= 0) return
    const targetX = -targetInternal * w
    if (animateRafRef.current != null) {
      cancelAnimationFrame(animateRafRef.current)
      animateRafRef.current = null
    }
    animatingRef.current = true
    const fallbackFromX = loop
      ? -internalSlideRef.current * w + dragPxRef.current
      : -activePhotoRef.current * w + dragPxRef.current
    const fromX = readCarouselTrackTranslateX(track, fallbackFromX)
    track.style.transition = 'none'
    track.style.transform = `translate3d(${fromX}px, 0, 0)`
    void track.getBoundingClientRect()
    animateRafRef.current = requestAnimationFrame(() => {
      animateRafRef.current = null
      track.style.transition = `transform 0.48s ${IOS_GALLERY_EASE}`
      track.style.transform = `translate3d(${targetX}px, 0, 0)`
    })
  }, [loop])

  const snapCloneIfNeeded = useCallback(() => {
    if (!loop || dragStartRef.current) return
    const n = photos.length
    if (n <= 1) return
    const internal = internalSlideRef.current
    if (internal === 0) {
      if (activePhotoRef.current === 0) {
        internalSlideRef.current = 1
        paintTrack(false)
        return
      }
      internalSlideRef.current = n
      const display = n - 1
      if (display !== activePhotoRef.current) onActivePhotoChange(display)
      paintTrack(false)
    } else if (internal === n + 1) {
      if (activePhotoRef.current === n - 1) {
        internalSlideRef.current = n
        paintTrack(false)
        return
      }
      internalSlideRef.current = 1
      if (activePhotoRef.current !== 0) onActivePhotoChange(0)
      paintTrack(false)
    }
  }, [loop, photos.length, paintTrack, onActivePhotoChange])

  const scheduleDragPaint = useCallback(() => {
    if (rafRef.current != null) return
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null
      paintTrack(false)
    })
  }, [paintTrack])

  useEffect(() => {
    const track = trackRef.current
    if (!track || !loop) return
    const onTransitionEnd = (ev: TransitionEvent) => {
      if (ev.propertyName !== 'transform') return
      animatingRef.current = false
      snapCloneIfNeeded()
    }
    track.addEventListener('transitionend', onTransitionEnd)
    return () => track.removeEventListener('transitionend', onTransitionEnd)
  }, [loop, photosKey, snapCloneIfNeeded])

  useEffect(() => {
    const track = trackRef.current
    if (!track || loop) return
    const onTransitionEnd = (ev: TransitionEvent) => {
      if (ev.propertyName !== 'transform') return
      animatingRef.current = false
    }
    track.addEventListener('transitionend', onTransitionEnd)
    return () => track.removeEventListener('transitionend', onTransitionEnd)
  }, [loop, photos.length])

  useLayoutEffect(() => {
    if (prevPhotosKeyRef.current !== photosKey) {
      prevPhotosKeyRef.current = photosKey
      const clamped =
        photos.length === 0 ? 0 : Math.min(Math.max(0, activePhoto), photos.length - 1)
      internalSlideRef.current = clamped
      prevSyncedActiveRef.current = clamped
      dragPxRef.current = 0
      lastDragPxRef.current = 0
      dragStartRef.current = null
      setIsDragging(false)
      animatingRef.current = false
      if (animateRafRef.current != null) {
        cancelAnimationFrame(animateRafRef.current)
        animateRafRef.current = null
      }
      if (clamped !== activePhoto) onActivePhotoChange(clamped)
      else paintTrack(false)
      return
    }
    if (photos.length <= 1) return
    if (dragStartRef.current) return

    const externalJump = prevSyncedActiveRef.current !== activePhoto
    prevSyncedActiveRef.current = activePhoto

    if (animatingRef.current && !externalJump) return

    if (loop) {
      const displayFromInternal = displayIndexFromInternal(internalSlideRef.current, photos.length)
      if (activePhoto !== displayFromInternal) {
        internalSlideRef.current = internalIndexFromDisplay(activePhoto, photos.length, true)
        dragPxRef.current = 0
        paintTrack(false)
      }
      return
    }

    if (externalJump) {
      animatingRef.current = false
      if (animateRafRef.current != null) {
        cancelAnimationFrame(animateRafRef.current)
        animateRafRef.current = null
      }
      const track = trackRef.current
      if (track) track.style.transition = 'none'
    }

    dragPxRef.current = 0
    paintTrack(false)
  }, [activePhoto, photosKey, viewportWidth, photos.length, loop, paintTrack, onActivePhotoChange])

  const commitSlide = useCallback(
    (nextInternal: number, animate: boolean) => {
      dragPxRef.current = 0
      lastDragPxRef.current = 0

      if (loop) {
        const n = photos.length
        let targetInternal = clampLoopInternal(nextInternal, n)
        const cur = internalSlideRef.current
        if (cur === n + 1 && targetInternal > n + 1) {
          internalSlideRef.current = 1
          paintTrack(false)
          targetInternal = 2
        } else if (cur === 0 && targetInternal < 0) {
          internalSlideRef.current = n
          paintTrack(false)
          targetInternal = n - 1
        }
        targetInternal = clampLoopInternal(targetInternal, n)
        const displayIdx = clampDisplayIndex(displayIndexFromInternal(targetInternal, n), n)
        if (animate) {
          animateTrackTo(targetInternal)
          internalSlideRef.current = targetInternal
          if (displayIdx !== activePhotoRef.current) onActivePhotoChange(displayIdx)
        } else {
          internalSlideRef.current = targetInternal
          paintTrack(false)
          snapCloneIfNeeded()
          if (displayIdx !== activePhotoRef.current) onActivePhotoChange(displayIdx)
        }
        return
      }

      const clamped = Math.max(0, Math.min(photos.length - 1, nextInternal))
      if (animate) {
        animateTrackTo(clamped)
        if (clamped !== activePhotoRef.current) onActivePhotoChange(clamped)
      } else if (clamped !== activePhotoRef.current) {
        onActivePhotoChange(clamped)
      } else {
        paintTrack(true)
      }
    },
    [loop, onActivePhotoChange, photos.length, paintTrack, animateTrackTo, snapCloneIfNeeded],
  )

  const finishDrag = useCallback(
    (dx: number, axis: 'x' | 'y' | null) => {
      const w = viewportWidthRef.current
      setIsDragging(false)

      const effectiveAxis = axis ?? (Math.abs(lastDragPxRef.current) > TAP_MOVE_MAX_PX ? 'x' : null)
      if (Math.abs(dx) > TAP_MOVE_MAX_PX || Math.abs(lastDragPxRef.current) > TAP_MOVE_MAX_PX) {
        suppressClickRef.current = true
      }

      if (effectiveAxis !== 'x' || w <= 0) {
        const hadOffset = Math.abs(dx) > 0 || Math.abs(lastDragPxRef.current) > 0
        dragPxRef.current = 0
        lastDragPxRef.current = 0
        const current = loop ? internalSlideRef.current : activePhotoRef.current
        commitSlide(current, hadOffset)
        return
      }

      const threshold = Math.max(SNAP_MIN_PX, w * SNAP_RATIO)
      if (loop) {
        let nextInternal = internalSlideRef.current
        if (dx <= -threshold) nextInternal += 1
        else if (dx >= threshold) nextInternal -= 1
        commitSlide(nextInternal, true)
        return
      }

      let next = activePhotoRef.current
      if (dx <= -threshold) next = Math.min(photos.length - 1, next + 1)
      else if (dx >= threshold) next = Math.max(0, next - 1)
      commitSlide(next, true)
    },
    [photos.length, commitSlide, loop],
  )

  function isDefectPinTarget(target: EventTarget | null) {
    return target instanceof HTMLElement && !!target.closest('.listing-detail-defect-pin-wrap')
  }

  function onDefectPinPointerDown(e: ReactPointerEvent<HTMLElement>) {
    e.stopPropagation()
  }

  function onPointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    if (photos.length <= 1) return
    if (isDefectPinTarget(e.target)) return
    if (e.pointerType === 'mouse' && e.button !== 0) return
    suppressClickRef.current = false
    dragStartRef.current = { x: e.clientX, y: e.clientY, pointerId: e.pointerId, axis: null }
    dragPxRef.current = 0
    lastDragPxRef.current = 0
    setIsDragging(true)
    paintTrack(false)
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  function onPointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    const start = dragStartRef.current
    if (!start || start.pointerId !== e.pointerId) return

    const dx = e.clientX - start.x
    const dy = e.clientY - start.y

    if (!start.axis) {
      if (Math.abs(dx) < AXIS_LOCK_PX && Math.abs(dy) < AXIS_LOCK_PX) return
      const axis: 'x' | 'y' = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y'
      dragStartRef.current = { ...start, axis }
      if (axis === 'y') {
        dragStartRef.current = null
        dragPxRef.current = 0
        setIsDragging(false)
        try {
          e.currentTarget.releasePointerCapture(e.pointerId)
        } catch {
          /* already released */
        }
        return
      }
    }

    if (dragStartRef.current?.axis === 'y') return

    e.preventDefault()
    e.stopPropagation()

    let dragPx = dx
    if (Math.abs(dragPx) > TAP_MOVE_MAX_PX) suppressClickRef.current = true

    if (!loop) {
      if (activePhotoRef.current === 0 && dragPx > 0) dragPx *= 0.28
      if (activePhotoRef.current === photos.length - 1 && dragPx < 0) dragPx *= 0.28
    }

    dragPxRef.current = dragPx
    lastDragPxRef.current = dragPx
    scheduleDragPaint()
  }

  function endPointer(e: ReactPointerEvent<HTMLDivElement>) {
    const start = dragStartRef.current
    if (!start || start.pointerId !== e.pointerId) return
    const dx = e.clientX - start.x
    const axis = start.axis
    dragStartRef.current = null
    try {
      e.currentTarget.releasePointerCapture(e.pointerId)
    } catch {
      /* already released */
    }
    finishDrag(dx, axis)
  }

  function onViewportClick(e: ReactMouseEvent<HTMLDivElement>) {
    if (staticPreview || !onPhotoClick) return
    if (isDefectPinTarget(e.target)) return
    if (suppressClickRef.current) {
      suppressClickRef.current = false
      e.preventDefault()
      e.stopPropagation()
      return
    }
    const slide = (e.target as HTMLElement).closest('[data-photo-index]')
    const raw = slide?.getAttribute('data-photo-index')
    const idx =
      raw != null && raw !== '' && !Number.isNaN(Number(raw))
        ? Number(raw)
        : activePhotoRef.current
    onPhotoClick(idx)
  }

  function defectPinKey(m: DefectMark, photoIdx: number, localIdx: number) {
    return `${photoIdx}-${m.x}-${m.y}-${localIdx}`
  }

  function onNavPointerDown(e: ReactPointerEvent<HTMLButtonElement>) {
    e.stopPropagation()
  }

  function onNavClick(e: ReactMouseEvent<HTMLButtonElement>, dir: -1 | 1) {
    e.preventDefault()
    e.stopPropagation()
    suppressClickRef.current = true
    setOpenDefectKey(null)
    if (loop) {
      commitSlide(internalSlideRef.current + dir, true)
    } else {
      commitSlide(activePhotoRef.current + dir, true)
    }
  }

  function onWheel(e: ReactWheelEvent<HTMLDivElement>) {
    if (photos.length <= 1) return
    const dx = e.deltaX
    const dy = e.deltaY
    if (Math.abs(dx) < 2 && Math.abs(dy) < 2) return
    if (Math.abs(dx) <= Math.abs(dy) * 1.15 && !e.shiftKey) return

    e.preventDefault()
    e.stopPropagation()
    suppressClickRef.current = true

    wheelAccumRef.current += e.shiftKey ? dy : Math.abs(dx) >= Math.abs(dy) ? dx : dy
    if (Math.abs(wheelAccumRef.current) < WHEEL_STEP_PX) return

    const dir = wheelAccumRef.current > 0 ? 1 : -1
    wheelAccumRef.current = 0
    if (loop) {
      commitSlide(internalSlideRef.current + dir, true)
    } else {
      commitSlide(activePhotoRef.current + dir, true)
    }
  }

  function slideContent(img: ListingCarouselPhoto, photoIdx: number) {
    const src = publicAssetUrl(img.display_url || img.thumbnail_url)
    const pins = defectMarks.filter((m) => (m.photo_index ?? 0) === photoIdx)
    return (
      <>
        <img src={src} alt="" draggable={false} className="listing-detail-main-img listing-detail-main-img--user" />
        {pins.map((m, i) => {
          const key = defectPinKey(m, photoIdx, i)
          const open = openDefectKey === key
          const num = globalDefectNumber(defectMarks, m)
          return (
            <div
              key={`defect-${key}`}
              className={`listing-detail-defect-pin-wrap${open ? ' listing-detail-defect-pin-wrap--open' : ''}`}
              style={{ left: `${m.x}%`, top: `${m.y}%` }}
            >
              <button
                type="button"
                className="listing-detail-defect-pin"
                aria-expanded={open}
                aria-label={m.label}
                onPointerDown={onDefectPinPointerDown}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  suppressClickRef.current = true
                  setOpenDefectKey((prev) => (prev === key ? null : key))
                }}
              >
                {num}
              </button>
              {open ? (
                <div className="listing-detail-defect-popover" role="tooltip">
                  {m.label}
                </div>
              ) : null}
            </div>
          )
        })}
        {!staticPreview && enlargeHint && photoIdx === activePhoto && !isDragging ? (
          <span className="listing-detail-photo-hint">{enlargeHint}</span>
        ) : null}
      </>
    )
  }

  if (photos.length === 0) return null

  if (photos.length === 1) {
    const img = photos[0]
    return (
      <div className="listing-detail-main-photo listing-detail-photo-carousel listing-detail-photo-carousel--single">
        {staticPreview ? (
          <div className="listing-detail-photo-open listing-detail-photo-open--static">
            {slideContent(img, 0)}
          </div>
        ) : (
          <div
            className="listing-detail-photo-open"
            role="button"
            tabIndex={0}
            aria-label={enlargeHint}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' && e.key !== ' ') return
              e.preventDefault()
              if (!isDefectPinTarget(e.target)) onPhotoClick?.(0)
            }}
            onClick={(e) => {
              if (isDefectPinTarget(e.target)) return
              onPhotoClick?.(0)
            }}
          >
            {slideContent(img, 0)}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="listing-detail-main-photo listing-detail-photo-carousel">
      <div
        ref={viewportRef}
        className={`listing-detail-photo-carousel__viewport${isDragging ? ' listing-detail-photo-carousel__viewport--dragging' : ''}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
        onClick={onViewportClick}
        onWheel={onWheel}
      >
        <div ref={trackRef} className="listing-detail-photo-carousel__track">
          {slidePhotos.map((img, idx) => {
            const photoIdx = loop ? displayIndexFromInternal(idx, photos.length) : idx
            return (
              <div
                key={`${img.id}-loop-${idx}`}
                data-photo-index={photoIdx}
                className={`listing-detail-carousel-slide${staticPreview ? ' listing-detail-carousel-slide--static' : ''}`}
              >
                <div
                  className={`listing-detail-photo-open${staticPreview ? ' listing-detail-photo-open--static' : ''}`}
                >
                  {slideContent(img, photoIdx)}
                </div>
              </div>
            )
          })}
        </div>
      </div>
      <button
        type="button"
        className="listing-detail-photo-carousel__nav listing-detail-photo-carousel__nav--prev"
        aria-label={t('listing.galleryPrev')}
        onPointerDown={onNavPointerDown}
        onClick={(e) => onNavClick(e, -1)}
      >
        <svg className="listing-detail-photo-carousel__nav-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M14.5 6.5 9 12l5.5 5.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.25"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <button
        type="button"
        className="listing-detail-photo-carousel__nav listing-detail-photo-carousel__nav--next"
        aria-label={t('listing.galleryNext')}
        onPointerDown={onNavPointerDown}
        onClick={(e) => onNavClick(e, 1)}
      >
        <svg className="listing-detail-photo-carousel__nav-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M9.5 6.5 15 12l-5.5 5.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.25"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <div className="listing-detail-dots" aria-hidden="true">
        {photos.map((p, idx) => (
          <span
            key={p.id}
            className={`listing-detail-dot${idx === activePhoto ? ' listing-detail-dot--active' : ''}`}
          />
        ))}
      </div>
    </div>
  )
}
